import { getUserLocales } from "get-user-locale";

export interface RegionalVariant {
  code: string;
  baseLanguage: string;
  region: string;
  country: string;
  displayName: string;
  nativeName: string;
  fallbackChain: string[];
}

export interface LocationInfo {
  timezone: string;
  country?: string;
  region?: string;
  currency?: string;
  locale?: string;
}

export interface RegionalDetectionResult {
  variant: RegionalVariant;
  confidence: number;
  source: "browser-locale" | "timezone" | "navigator-language" | "fallback";
  detectedLocation?: LocationInfo;
}

export const REGIONAL_VARIANTS: Record<string, RegionalVariant> = {
  "en-US": {
    code: "en-US",
    baseLanguage: "en",
    region: "US",
    country: "United States",
    displayName: "English (US)",
    nativeName: "English (United States)",
    fallbackChain: ["en-US", "en", "en-GB"],
  },
  "en-GB": {
    code: "en-GB",
    baseLanguage: "en",
    region: "GB",
    country: "United Kingdom",
    displayName: "English (UK)",
    nativeName: "English (United Kingdom)",
    fallbackChain: ["en-GB", "en", "en-US"],
  },
  "en-CA": {
    code: "en-CA",
    baseLanguage: "en",
    region: "CA",
    country: "Canada",
    displayName: "English (Canada)",
    nativeName: "English (Canada)",
    fallbackChain: ["en-CA", "en-US", "en", "en-GB"],
  },
  "en-AU": {
    code: "en-AU",
    baseLanguage: "en",
    region: "AU",
    country: "Australia",
    displayName: "English (Australia)",
    nativeName: "English (Australia)",
    fallbackChain: ["en-AU", "en-GB", "en", "en-US"],
  },
  "de-DE": {
    code: "de-DE",
    baseLanguage: "de",
    region: "DE",
    country: "Germany",
    displayName: "German (Germany)",
    nativeName: "Deutsch (Deutschland)",
    fallbackChain: ["de-DE", "de", "de-AT", "de-CH"],
  },
  "de-AT": {
    code: "de-AT",
    baseLanguage: "de",
    region: "AT",
    country: "Austria",
    displayName: "German (Austria)",
    nativeName: "Deutsch (Österreich)",
    fallbackChain: ["de-AT", "de-DE", "de", "de-CH"],
  },
  "de-CH": {
    code: "de-CH",
    baseLanguage: "de",
    region: "CH",
    country: "Switzerland",
    displayName: "German (Switzerland)",
    nativeName: "Deutsch (Schweiz)",
    fallbackChain: ["de-CH", "de-DE", "de", "de-AT"],
  },
  uk: {
    code: "uk",
    baseLanguage: "uk",
    region: "UA",
    country: "Ukraine",
    displayName: "Ukrainian (Ukraine)",
    nativeName: "Українська (Україна)",
    fallbackChain: ["uk"],
  },
  pl: {
    code: "pl",
    baseLanguage: "pl",
    region: "PL",
    country: "Poland",
    displayName: "Polish (Poland)",
    nativeName: "Polski (Polska)",
    fallbackChain: ["pl"],
  },
  "ja-JP": {
    code: "ja-JP",
    baseLanguage: "ja",
    region: "JP",
    country: "Japan",
    displayName: "Japanese (Japan)",
    nativeName: "日本語 (日本)",
    fallbackChain: ["ja-JP", "ja"],
  },
  ar: {
    code: "ar",
    baseLanguage: "ar",
    region: "SA",
    country: "Saudi Arabia",
    displayName: "Arabic",
    nativeName: "العربية",
    fallbackChain: ["ar"],
  },
};

const TIMEZONE_TO_REGION: Record<string, string> = {
  // United States
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Anchorage": "US",
  "America/Honolulu": "US",
  "America/Phoenix": "US",
  "America/Detroit": "US",

  // Canada
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Montreal": "CA",
  "America/Halifax": "CA",
  "America/Winnipeg": "CA",
  "America/Edmonton": "CA",

  // United Kingdom & Ireland
  "Europe/London": "GB",
  "Europe/Belfast": "GB",
  "Europe/Edinburgh": "GB",
  "Europe/Cardiff": "GB",
  "Europe/Dublin": "IE",
  "Europe/Isle_of_Man": "GB",
  "Europe/Jersey": "GB",
  "Europe/Guernsey": "GB",

  // Germany
  "Europe/Berlin": "DE",
  "Europe/Munich": "DE",
  "Europe/Hamburg": "DE",
  "Europe/Cologne": "DE",

  // Austria
  "Europe/Vienna": "AT",
  "Europe/Salzburg": "AT",
  "Europe/Innsbruck": "AT",

  // Switzerland
  "Europe/Zurich": "CH",
  "Europe/Geneva": "CH",
  "Europe/Basel": "CH",

  // Belgium
  "Europe/Brussels": "BE",
  "Europe/Antwerp": "BE",

  // Netherlands
  "Europe/Amsterdam": "NL",
  "Europe/Rotterdam": "NL",

  // Other European
  "Europe/Kiev": "UA",
  "Europe/Kyiv": "UA",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Budapest": "HU",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Lisbon": "PT",
  "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI",

  // Japan
  "Asia/Tokyo": "JP",
  "Asia/Osaka": "JP",

  // Australia
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Perth": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Adelaide": "AU",
  "Australia/Darwin": "AU",
  "Australia/Hobart": "AU",
};

const COUNTRY_TO_LANGUAGE_PREFERENCE: Record<string, string[]> = {
  US: ["en-US", "en"],
  GB: ["en-GB", "en"],
  CA: ["en-CA", "en"],
  AU: ["en-AU", "en"],
  DE: ["de-DE", "de"],
  AT: ["de-AT", "de"],
  CH: ["de-CH", "de"],
  BE: ["nl-BE", "nl"],
  UA: ["uk"],
  PL: ["pl"],
  IE: ["en-IE", "en"],
  JP: ["ja-JP", "ja"],
};

export const detectLocationInfo = (): LocationInfo => {
  const info: LocationInfo = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  try {
    // Get locale information
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (locale) {
      info.locale = locale;
      const parts = locale.split("-");
      if (parts.length > 1) {
        info.country = parts[1].toUpperCase();
      }
    }

    // Get currency information
    const currency = Intl.NumberFormat().resolvedOptions().currency;
    if (currency) {
      info.currency = currency;
    }

    // Enhanced timezone-based region detection
    if (info.timezone) {
      // Direct timezone mapping
      if (TIMEZONE_TO_REGION[info.timezone]) {
        info.country = info.country || TIMEZONE_TO_REGION[info.timezone];
        info.region = TIMEZONE_TO_REGION[info.timezone];
      }

      // Special handling for UK timezones
      if (
        info.timezone === "Europe/London" ||
        (info.timezone.startsWith("Europe/") &&
          [
            "Belfast",
            "Edinburgh",
            "Cardiff",
            "Isle_of_Man",
            "Jersey",
            "Guernsey",
          ].some((city) => info.timezone.includes(city)))
      ) {
        info.country = "GB";
        info.region = "GB";
      }

      // Handle GMT timezone (often used in UK)
      if (info.timezone === "GMT" || info.timezone === "UTC") {
        // Check if currency or locale suggests UK
        if (
          info.currency === "GBP" ||
          info.locale?.includes("GB") ||
          info.locale?.includes("en-GB")
        ) {
          info.country = "GB";
          info.region = "GB";
        }
      }
    }

    // Currency-based country detection
    if (info.currency && !info.country) {
      const currencyToCountry: Record<string, string> = {
        USD: "US",
        GBP: "GB",
        EUR: "DE",
        CAD: "CA",
        AUD: "AU",
        CHF: "CH",
        UAH: "UA",
        PLN: "PL",
      };

      if (currencyToCountry[info.currency]) {
        info.country = currencyToCountry[info.currency];
        info.region = currencyToCountry[info.currency];
      }
    }

    // Locale-based country detection (fallback)
    if (!info.country && info.locale) {
      const localeMatch = info.locale.match(/[_-]([A-Z]{2})$/);
      if (localeMatch) {
        info.country = localeMatch[1];
        info.region = localeMatch[1];
      }
    }
  } catch (error) {
    console.warn("Failed to detect location info:", error);
  }

  return info;
};

export const getBrowserLanguageVariants = (): string[] => {
  try {
    const userLocales = getUserLocales({
      fallbackLocale: "en-US",
      useFallbackLocale: true,
    });

    return userLocales.filter(Boolean);
  } catch (error) {
    console.warn(
      "Failed to get user locales from get-user-locale, falling back to manual detection:",
      error
    );

    if (typeof window === "undefined") return ["en-US"];

    const languages: string[] = [];

    if (navigator.languages && navigator.languages.length > 0) {
      languages.push(...navigator.languages);
    }

    if (navigator.language) {
      languages.push(navigator.language);
    }

    if ((navigator as any).userLanguage) {
      languages.push((navigator as any).userLanguage);
    }

    if ((navigator as any).browserLanguage) {
      languages.push((navigator as any).browserLanguage);
    }

    if ((navigator as any).systemLanguage) {
      languages.push((navigator as any).systemLanguage);
    }

    return Array.from(new Set(languages)).filter(Boolean);
  }
};

export const normalizeVariantCode = (code: string): string => {
  if (!code) return "";

  const normalized = code.replace("_", "-");
  const parts = normalized.split("-");

  if (parts.length >= 2) {
    return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
  }

  return parts[0].toLowerCase();
};

export const getBaseLanguage = (variantCode: string): string => {
  return variantCode.split("-")[0].toLowerCase();
};

export const detectRegionalVariant = (
  supportedVariants: string[],
  supportedBaseLanguages: string[]
): RegionalDetectionResult => {
  const locationInfo = detectLocationInfo();
  const browserLanguages = getBrowserLanguageVariants();

  // Step 1: Identify the current browser's preferred regional variant
  let detectedVariant: RegionalVariant | null = null;
  let detectionSource: "browser-locale" | "timezone" | "navigator-language" | "fallback" = "fallback";
  let confidence = 0.1;

  // Try to detect from browser languages (exact match first)
  for (const browserLang of browserLanguages) {
    const normalizedVariant = normalizeVariantCode(browserLang);
    if (REGIONAL_VARIANTS[normalizedVariant]) {
      detectedVariant = REGIONAL_VARIANTS[normalizedVariant];
      detectionSource = "browser-locale";
      confidence = 0.95;
      break;
    }
  }

  // If no exact match, try to construct variant from browser language + detected region
  if (!detectedVariant && locationInfo.country) {
    for (const browserLang of browserLanguages) {
      const baseLanguage = getBaseLanguage(browserLang);
      const potentialVariant = `${baseLanguage}-${locationInfo.country}`;
      
      if (REGIONAL_VARIANTS[potentialVariant]) {
        detectedVariant = REGIONAL_VARIANTS[potentialVariant];
        detectionSource = "navigator-language";
        confidence = 0.8;
        break;
      }
    }
  }

  // Try timezone-based detection with browser language
  if (!detectedVariant && locationInfo.country) {
    const countryPreferences = COUNTRY_TO_LANGUAGE_PREFERENCE[locationInfo.country] || [];
    
    for (const browserLang of browserLanguages) {
      const baseLanguage = getBaseLanguage(browserLang);
      const matchingPreference = countryPreferences.find(pref => 
        getBaseLanguage(pref) === baseLanguage
      );
      
      if (matchingPreference && REGIONAL_VARIANTS[matchingPreference]) {
        detectedVariant = REGIONAL_VARIANTS[matchingPreference];
        detectionSource = "timezone";
        confidence = 0.75;
        break;
      }
    }
  }

  // Fallback to base language default variants
  if (!detectedVariant) {
    for (const browserLang of browserLanguages) {
      const baseLanguage = getBaseLanguage(browserLang);
      const defaultVariants = Object.values(REGIONAL_VARIANTS).filter(
        v => v.baseLanguage === baseLanguage
      );

      if (defaultVariants.length > 0) {
        let defaultVariant = defaultVariants[0];

        if (baseLanguage === "en") {
          defaultVariant = defaultVariants.find(v => v.code === "en-US") || defaultVariant;
        } else if (baseLanguage === "de") {
          defaultVariant = defaultVariants.find(v => v.code === "de-DE") || defaultVariant;
        } else if (baseLanguage === "uk") {
          defaultVariant = defaultVariants.find(v => v.code === "uk") || defaultVariant;
        }

        detectedVariant = defaultVariant;
        confidence = 0.6;
        break;
      }
    }
  }

  // Final fallback
  if (!detectedVariant) {
    detectedVariant = REGIONAL_VARIANTS["en-US"];
  }

  // Step 2: Check if the detected variant is supported
  if (supportedVariants.includes(detectedVariant!.code)) {
    return {
      variant: detectedVariant!,
      confidence,
      source: detectionSource,
      detectedLocation: locationInfo,
    };
  }

  // Step 3: Check if base language is supported and find best supported variant
  if (supportedBaseLanguages.includes(detectedVariant!.baseLanguage)) {
    const supportedVariantsForLanguage = Object.values(REGIONAL_VARIANTS).filter(
      v => v.baseLanguage === detectedVariant!.baseLanguage && supportedVariants.includes(v.code)
    );

    if (supportedVariantsForLanguage.length > 0) {
      let bestSupportedVariant = supportedVariantsForLanguage[0];

      // Prefer certain defaults per language
      if (detectedVariant!.baseLanguage === "en") {
        bestSupportedVariant = supportedVariantsForLanguage.find(v => v.code === "en-US") || bestSupportedVariant;
      } else if (detectedVariant!.baseLanguage === "de") {
        bestSupportedVariant = supportedVariantsForLanguage.find(v => v.code === "de-DE") || bestSupportedVariant;
      } else if (detectedVariant!.baseLanguage === "uk") {
        bestSupportedVariant = supportedVariantsForLanguage.find(v => v.code === "uk") || bestSupportedVariant;
      }

      return {
        variant: bestSupportedVariant,
        confidence: Math.max(0.4, confidence - 0.2),
        source: "fallback",
        detectedLocation: locationInfo,
      };
    }
  }

  // Step 4: Return first supported variant as final fallback
  if (supportedVariants.length > 0) {
    const firstSupportedVariant = supportedVariants[0];
    if (REGIONAL_VARIANTS[firstSupportedVariant]) {
      return {
        variant: REGIONAL_VARIANTS[firstSupportedVariant],
        confidence: 0.1,
        source: "fallback",
        detectedLocation: locationInfo,
      };
    }
  }

  return {
    variant: REGIONAL_VARIANTS["en-US"],
    confidence: 0.1,
    source: "fallback",
    detectedLocation: locationInfo,
  };
};

export const getVariantsByBaseLanguage = (
  baseLanguage: string
): RegionalVariant[] => {
  return Object.values(REGIONAL_VARIANTS).filter(
    (variant) => variant.baseLanguage === baseLanguage
  );
};

export const getSupportedVariants = (): string[] => {
  return Object.keys(REGIONAL_VARIANTS);
};

export const getVariantDisplayName = (
  variantCode: string,
  displayLanguage?: string
): string => {
  const variant = REGIONAL_VARIANTS[variantCode];
  if (!variant) return variantCode;

  if (displayLanguage && displayLanguage !== variantCode) {
    try {
      const displayNames = new Intl.DisplayNames([displayLanguage], {
        type: "language",
        languageDisplay: "standard",
      });
      const regionDisplayNames = new Intl.DisplayNames([displayLanguage], {
        type: "region",
      });

      const languageName = displayNames.of(variant.baseLanguage);
      const regionName = regionDisplayNames.of(variant.region);

      if (languageName && regionName) {
        return `${languageName} (${regionName})`;
      }
    } catch (error) {
      console.warn("Failed to get display name:", error);
    }
  }

  return variant.displayName;
};

export const isVariantSupported = (
  variantCode: string,
  supportedVariants: string[]
): boolean => {
  return supportedVariants.includes(variantCode);
};

export const getFallbackChain = (variantCode: string): string[] => {
  const variant = REGIONAL_VARIANTS[variantCode];
  return variant ? variant.fallbackChain : [variantCode];
};

export const detectRegionalVariantForLanguage = (
  targetLanguage: string,
  supportedVariants: string[]
): RegionalDetectionResult => {
  const locationInfo = detectLocationInfo();

  // Filter variants for the target language
  const targetVariants = Object.values(REGIONAL_VARIANTS).filter(
    (v) =>
      v.baseLanguage === targetLanguage && supportedVariants.includes(v.code)
  );

  if (targetVariants.length === 0) {
    throw new Error(
      `No supported variants found for language: ${targetLanguage}`
    );
  }

  // Step 1: Check browser languages for exact matches with target language
  const browserLanguages = getBrowserLanguageVariants();
  for (const browserLang of browserLanguages) {
    const normalizedVariant = normalizeVariantCode(browserLang);
    const baseLanguage = getBaseLanguage(normalizedVariant);

    if (
      baseLanguage === targetLanguage &&
      supportedVariants.includes(normalizedVariant) &&
      REGIONAL_VARIANTS[normalizedVariant]
    ) {
      return {
        variant: REGIONAL_VARIANTS[normalizedVariant],
        confidence: 0.95,
        source: "browser-locale",
        detectedLocation: locationInfo,
      };
    }
  }

  // Step 2: Timezone-based detection for target language
  if (locationInfo.timezone && locationInfo.country) {
    const countryPreferences =
      COUNTRY_TO_LANGUAGE_PREFERENCE[locationInfo.country] || [];

    for (const preference of countryPreferences) {
      const baseLanguage = getBaseLanguage(preference);
      if (
        baseLanguage === targetLanguage &&
        supportedVariants.includes(preference) &&
        REGIONAL_VARIANTS[preference]
      ) {
        return {
          variant: REGIONAL_VARIANTS[preference],
          confidence: 0.85,
          source: "timezone",
          detectedLocation: locationInfo,
        };
      }
    }
  }

  // Step 3: Smart default selection for target language
  let defaultVariant = targetVariants[0];

  if (targetLanguage === "en") {
    if (
      locationInfo.timezone === "Europe/London" ||
      locationInfo.country === "GB"
    ) {
      defaultVariant =
        targetVariants.find((v) => v.code === "en-GB") || defaultVariant;
    } else {
      defaultVariant =
        targetVariants.find((v) => v.code === "en-US") || defaultVariant;
    }
  } else if (targetLanguage === "de") {
    if (
      locationInfo.timezone?.includes("Vienna") ||
      locationInfo.country === "AT"
    ) {
      defaultVariant =
        targetVariants.find((v) => v.code === "de-AT") || defaultVariant;
    } else if (
      locationInfo.timezone?.includes("Zurich") ||
      locationInfo.country === "CH"
    ) {
      defaultVariant =
        targetVariants.find((v) => v.code === "de-CH") || defaultVariant;
    } else {
      defaultVariant =
        targetVariants.find((v) => v.code === "de-DE") || defaultVariant;
    }
  } else if (targetLanguage === "uk") {
    defaultVariant =
      targetVariants.find((v) => v.code === "uk-UA") || defaultVariant;
  }

  return {
    variant: defaultVariant,
    confidence: 0.7,
    source: "fallback",
    detectedLocation: locationInfo,
  };
};
